import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Plus, CalendarDays, Clock } from 'lucide-react';
import HelpTooltip from '../common/HelpTooltip';
import ReportDefinitionList from './ReportDefinitionList';
import ReportExecutionHistory from './ReportExecutionHistory';
import ReportDefinitionForm from './ReportDefinitionForm';
import ReportViewer from './ReportViewer';
import { useReportDefinitions, useReportExecutions } from '../../hooks/useScheduledReports';
import { getClients } from '../../services/api';
import { ReportType, ReportExecutionStatus, ReportExecution, Client } from '../../types';

export default function ScheduledReportsPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingExecution, setViewingExecution] = useState<ReportExecution | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [activeTab, setActiveTab] = useState('definitions');

  // Filters for definitions
  const [defClientFilter, setDefClientFilter] = useState<string>('');
  const [defReportTypeFilter, setDefReportTypeFilter] = useState<string>('');
  const [defEnabledFilter, setDefEnabledFilter] = useState<string>('');

  // Filters for executions
  const [execClientFilter, setExecClientFilter] = useState<string>('');
  const [execReportTypeFilter, setExecReportTypeFilter] = useState<string>('');
  const [execStatusFilter, setExecStatusFilter] = useState<string>('');
  const [execTriggerFilter, setExecTriggerFilter] = useState<string>('');

  const {
    definitions,
    total: definitionsTotal,
    loading: definitionsLoading,
    error: definitionsError,
    setFilters: setDefinitionFilters,
    refetch: refetchDefinitions,
  } = useReportDefinitions();

  const {
    executions,
    total: executionsTotal,
    loading: executionsLoading,
    error: executionsError,
    setFilters: setExecutionFilters,
    refetch: refetchExecutions,
  } = useReportExecutions();

  // Fetch clients for filter dropdown
  useEffect(() => {
    getClients({ pageSize: 500 }).then(result => {
      setClients(result.items);
    }).catch(console.error);
  }, []);

  // Apply definition filters when they change
  useEffect(() => {
    setDefinitionFilters(prev => ({
      ...prev,
      clientId: defClientFilter || undefined,
      reportType: (defReportTypeFilter as ReportType) || undefined,
      enabled: defEnabledFilter === '' ? undefined : defEnabledFilter === 'true',
      page: 1,
    }));
  }, [defClientFilter, defReportTypeFilter, defEnabledFilter, setDefinitionFilters]);

  // Apply execution filters when they change
  useEffect(() => {
    setExecutionFilters(prev => ({
      ...prev,
      clientId: execClientFilter || undefined,
      reportType: (execReportTypeFilter as ReportType) || undefined,
      status: (execStatusFilter as ReportExecutionStatus) || undefined,
      triggeredBy: (execTriggerFilter as 'schedule' | 'manual') || undefined,
      page: 1,
    }));
  }, [execClientFilter, execReportTypeFilter, execStatusFilter, execTriggerFilter, setExecutionFilters]);

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    refetchDefinitions();
  };

  const handleEditSuccess = () => {
    setEditingId(null);
    refetchDefinitions();
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  const handleDelete = () => {
    refetchDefinitions();
    refetchExecutions();
  };

  // Called after a report is run - refresh both lists
  const handleReportRun = () => {
    refetchDefinitions();
    refetchExecutions();
  };

  // View a report execution in the modal
  const handleViewReport = (execution: ReportExecution) => {
    setViewingExecution(execution);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              Security Reports
            </h1>
            <HelpTooltip
              text="Configure automated security reports for your clients. Reports include MFA status, license utilization, admin roles, and more. Schedule them to run daily, weekly, or monthly, with optional ITGlue upload."
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage automated security and compliance reports
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Report
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full space-x-1 rounded-xl bg-muted p-1 h-auto">
          <TabsTrigger
            value="definitions"
            className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors focus:outline-none data-[state=active]:bg-card data-[state=active]:text-primary-700 dark:data-[state=active]:text-primary-400 data-[state=active]:shadow text-muted-foreground hover:bg-card/50 hover:text-foreground"
          >
            <div className="flex items-center justify-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>Definitions</span>
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {definitionsTotal}
              </span>
            </div>
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors focus:outline-none data-[state=active]:bg-card data-[state=active]:text-primary-700 dark:data-[state=active]:text-primary-400 data-[state=active]:shadow text-muted-foreground hover:bg-card/50 hover:text-foreground"
          >
            <div className="flex items-center justify-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Execution History</span>
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {executionsTotal}
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="definitions" className="mt-4">
          <ReportDefinitionList
            definitions={definitions}
            loading={definitionsLoading}
            error={definitionsError}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRefresh={refetchDefinitions}
            onReportRun={handleReportRun}
            clients={clients}
            clientFilter={defClientFilter}
            setClientFilter={setDefClientFilter}
            reportTypeFilter={defReportTypeFilter}
            setReportTypeFilter={setDefReportTypeFilter}
            enabledFilter={defEnabledFilter}
            setEnabledFilter={setDefEnabledFilter}
          />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <ReportExecutionHistory
            executions={executions}
            loading={executionsLoading}
            error={executionsError}
            onRefresh={refetchExecutions}
            onViewReport={handleViewReport}
            clients={clients}
            clientFilter={execClientFilter}
            setClientFilter={setExecClientFilter}
            reportTypeFilter={execReportTypeFilter}
            setReportTypeFilter={setExecReportTypeFilter}
            statusFilter={execStatusFilter}
            setStatusFilter={setExecStatusFilter}
            triggerFilter={execTriggerFilter}
            setTriggerFilter={setExecTriggerFilter}
          />
        </TabsContent>
      </Tabs>

      {/* Create Form Modal */}
      {showCreateForm && (
        <ReportDefinitionForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit Form Modal */}
      {editingId && (
        <ReportDefinitionForm
          definitionId={editingId}
          onClose={() => setEditingId(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Report Viewer Modal */}
      <ReportViewer
        execution={viewingExecution}
        isOpen={!!viewingExecution}
        onClose={() => setViewingExecution(null)}
      />
    </div>
  );
}
